(ns app.front.view.group.admin.inspections.checklists.checklist-items
  "Group admin configuration for inspection checklist item templates and item groups.
  See `app.front.view.group.admin.inspections.checklists` for the config of the checklist template itself"
  (:require [app.front.entities :as entities]
            [app.front.entity-edit :as ee]
            [app.front.ui :as ui]
            [app.front.ui.dnd :as dnd]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.icon-button :as ui-ib]
            [app.front.ui.inline-editable :as inline-editable]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.separator :as ui-sep]
            [app.front.util :as futil]
            [app.front.view.group.admin.inspections.checklists.subs :as checklist-subs]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [app.rich-text :as rich-text]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(defn localized-attr [entity attr & [language]]
  (i18n-model/get-text (get entity attr)
                       (or language
                           (<sub [::ui-i18n/language-override]))))


;;; Subscriptions
;;;;;;;;;;;;;;;;;

(rf/reg-sub ::item-group-templates
  :<- [::checklist-subs/selected-checklist-template]
  (fn [checklist _]
    (when-let [checklist-id (:db/id checklist)]
      (sort-by :ordered/order
               (<sub [::entities/entities
                      {:entity/parent checklist-id
                       :entity/type   :entity.type/inspection-checklist-item-group-template}])))))

(rf/reg-sub ::prev-item-group-template
  :<- [::item-group-templates]
  (fn [item-groups [_ order]]
    (p/find-first (reverse item-groups)
                  #(> order (:ordered/order %)))))

(rf/reg-sub ::next-item-group-template
  :<- [::item-group-templates]
  (fn [item-groups [_ order]]
    (p/find-first item-groups
                  #(< order (:ordered/order %)))))

(rf/reg-sub ::group-template-items
  :<- [::checklist-subs/selected-checklist-template]
  (fn [checklist [_ item-group-id]]
    (when-let [checklist-id (:db/id checklist)]
      (sort-by :ordered/order
               (<sub [::entities/entities
                      {:entity/parent                   checklist-id
                       :entity/type                     :entity.type/inspection-checklist-item-template
                       :inspection.checklist-item/group item-group-id}])))))

(rf/reg-sub ::group-template-item-count
  (fn [_ [_ item-group-id]]
    (count (<sub [::group-template-items item-group-id]))))


;;; Events
;;;;;;;;;;

(rf/reg-event-fx ::create-item-group-template
  (fn [_ [_ checklist-id language]]
    {:comm/command {:command    :inspection/create-checklist-item-group-template
                    :data       {:id       checklist-id
                                 :language language}
                    :on-success [::create-item-group-template.success]}}))

(rf/reg-event-fx ::create-item-group-template.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [:notifications/add :toast :success :success :inspection.checklist-item.group/created]]}))

(rf/reg-event-fx ::delete-item-group-template
  (fn [_ [_ item-group-id]]
    {:comm/command {:command    :inspection/delete-checklist-item-group-template
                    :data       {:id item-group-id}
                    :on-success [::delete-item-group-template.success]}}))

(rf/reg-event-fx ::delete-item-group-template.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [:notifications/add :toast :success :success :inspection.checklist-item.group/deleted]]}))

(rf/reg-event-fx ::reorder-item-group-templates
  (fn [_ [_ from to]]
    {:comm/command {:command    :inspection/reorder-checklist-item-group-templates
                    :data       {:item-group-templates (dnd/ordering-tx-for-entity from to)}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::create-item-template
  (fn [_ [_ checklist-id item-group-id language]]
    {:comm/command {:command    :inspection/create-checklist-item-template
                    :data       {:id                      checklist-id
                                 :checklist-item-group-id item-group-id
                                 :language                language}
                    :on-success [::create-item-template.success]}}))

(rf/reg-event-fx ::create-item-template.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [::ee/set-values
                   (entities/id-of-some-in-apply-changes :entity.type/inspection-checklist-item-template response)
                   {}]
                  [:notifications/add :toast :success :success :inspection.checklist-item/created]]}))


(rf/reg-event-fx ::delete-item-template
  (fn [_ [_ item-id]]
    {:comm/command {:command    :inspection/delete-checklist-item-template
                    :data       {:id item-id}
                    :on-success [::delete-item-template.success]}}))

(rf/reg-event-fx ::delete-item-template.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [:notifications/add :toast :success :success :inspection.checklist-item/deleted]]}))


(rf/reg-event-fx ::reorder-item-templates
  (fn [_ [_ from to insert-before?]]
    {:comm/command {:command    :inspection/reorder-checklist-item-templates
                    :data       {:item-templates (dnd/ordering-tx-for-entity from to)
                                 :insert-before? insert-before?}
                    :on-success [::entities/apply-changes]}}))


(rf/reg-event-fx ::move-item-template
  (fn [_ [_ item-group-id item-id]]
    {:comm/command {:command    :inspection/move-checklist-item-template
                    :data       {:item-template-id       item-id
                                 :item-group-template-id item-group-id}
                    :on-success [::entities/apply-changes]}}))


;;; Components
;;;;;;;;;;;;;;

(defn item-column-headers
  "Has to have the same aligned grid as item-editor below"
  [{:keys [tr]}]
  [:div.mt-6.grid.gap-4.grid-cols-12.text-gray-600
   [:div.col-span-2 (tr [:inspection.checklist-item/code])]
   [:div.col-span-10 (tr [:inspection.checklist-item/descriptions])]])

(defn item-editor
  "A drag and drop reorderable editor for a single checklist item template"
  [{:keys [tr language container-opts draggable-opts]} item]
  (let [item-id      (:db/id item)
        unsaved-item (<sub [::ee/unsaved-for item-id])
        reordering?  (<sub [::checklist-subs/reordering?])
        editing?     (and unsaved-item
                          (not reordering?))
        value        #(or (get unsaved-item %)
                          (get item %))
        on-edit      #(rf/dispatch-sync [::ee/set-value item-id %1 %2])
        rt-enabled?  (<sub [::checklist-subs/checklist-configuration-v2])
        group-id     (<sub [::gas/group-id])
        align-class  (if unsaved-item "self-auto" "self-center")]
    (if editing?
      [:form.p-3.my-2.grid.gap-4.grid-cols-12.bg-gray-200.rounded-md
       {:on-submit (fn [evt]
                     (.preventDefault evt)
                     (>evt [::ee/save-changes nil :inspection/edit-checklist-item-template item-id]))}
       [:div.col-span-9
        {:class align-class}
        (let [editor-args {:id         (str "checklist-item-template-code-editor-" item-id)
                           :label-text (tr [:inspection.checklist-item/code])}]
          (if rt-enabled?
            [ui-rt/editor-legacy  (merge editor-args
                                         {:authority-group-id group-id
                                          :initial-value      (-> :inspection.checklist-item/code value)
                                          :placeholder        :inspection.checklist-item/code-placeholder
                                          :on-change          #(on-edit :inspection.checklist-item/code %)})]
            [ui/text-input (merge editor-args
                                  {:value       (-> :inspection.checklist-item/code value)
                                   :placeholder (tr [:inspection.checklist-item/code-placeholder])
                                   :on-change   #(on-edit :inspection.checklist-item/code (futil/tval %))})]))]
       [:div.col-span-3
        [:div.flex.justify-end.gap-4
         (cc/button {:kind     :ghost
                     :size     :small
                     :on-click #(>evt [::ee/cancel-changes item-id])}
                    (tr [:cancel]))
         (cc/button {:type     :submit
                     :kind     :primary
                     :size     :small
                     :disabled (or (empty? unsaved-item)
                                   (str/blank? (value :inspection.checklist-item/descriptions)))}
                    (tr [:save]))]]
       [:div.col-span-9
        {:class align-class}
        (let [editor-args {:id                  (str "checklist-item-template-description-editor-" item-id)
                           :label-text          (tr [:inspection.checklist-item/descriptions])
                           :on-change           #(on-edit :inspection.checklist-item/descriptions %)
                           :allowed-locales-sub ::gas/allowed-locales}
              strings     (or (:inspection.checklist-item/descriptions unsaved-item)
                              (-> item :inspection.checklist-item/descriptions i18n-model/strings-map))]
          (if rt-enabled?
            [ui-i18n/localized-rich-text-editor (merge editor-args (util/mmap {:authority-group-id group-id
                                                                               :placeholder        :inspection.checklist-item/description-placeholder}
                                                                              strings))]
            [ui-i18n/localized-string-edit (merge editor-args
                                                  {:multiline?  true
                                                   :placeholder (tr [:inspection.checklist-item/description-placeholder])
                                                   :strings     strings})]))]]
      [:div.p-3.my-2.grid.gap-4.grid-cols-12.bg-gray-200.rounded-md
       (r/merge-props container-opts (when reordering? draggable-opts))
       [:div.col-span-2
        {:class align-class}
        (if rt-enabled?
          [ui-rt/editor-legacy {:id            (str "checklist-item-template-code-" item-id)
                                :initial-value (value :inspection.checklist-item/code)}]
          (-> item :inspection.checklist-item/code rich-text/join-rich-text-content))]
       [:div.col-span-7
        {:class align-class}
        (if rt-enabled?
          [ui-i18n/localized-rich-text-editor
           {:id                  (str "checklist-item-template-description-" item-id)
            :strings             (or (:inspection.checklist-item/descriptions unsaved-item)
                                     (-> item :inspection.checklist-item/descriptions i18n-model/strings-map))
            :allowed-locales-sub ::gas/allowed-locales}]
          (rich-text/join-rich-text-content (localized-attr item :inspection.checklist-item/descriptions language)))]
       [:div.col-span-3
        (if reordering?
          [:div.p-2.flex.justify-end
           (cc/icon {:icon        i/draggable
                     :height      "1em"
                     :description (tr [:reorder])})]
          [:div.flex.justify-end.gap-4
           (cc/button {:kind     :ghost
                       :size     :small
                       :on-click #(>evt [::ee/set-values item-id {}])}
                      (tr [:edit]))
           [ui/modal-confirmation-button
            {:tr                tr
             :id                (str "delete-checklist-template-child-" item-id)
             :on-yes-click      #(>evt [::delete-item-template item-id])
             :icon-button?      true
             :trigger-icon-opts {:icon        i/trash
                                 :description (tr [:delete])
                                 :fill        "var(--ui-violet-100)"}}]])]])))

(defn i18n-popup-text-editor [{:keys [tr title-kw rename-kw entity attr save-command editing-disabled?]}]
  (let [entity-id   (:db/id entity)
        entity-attr (-> entity attr i18n-model/strings-map)
        unsaved     (<sub [::ee/unsaved-for entity-id])
        on-edit     #(rf/dispatch-sync [::ee/set-value entity-id %1 %2])
        disabled?   (= entity-attr (attr unsaved))]
    (if (attr unsaved)
      [inline-editable/popup-wrapper
       {:tr    tr
        :title title-kw}
       [inline-editable/i18n-text-field-confirm
        {:tr           tr
         :input-opts   {:id                  (str/join "-" ["edit" (namespace attr) (name attr) entity-id])
                        :auto-focus          true
                        :allowed-locales-sub ::gas/allowed-locales
                        :default-value       (or (attr unsaved) entity-attr)
                        :on-change           #(on-edit attr %)}
         :disabled     disabled?
         :on-confirm   #(>evt [::ee/save-changes nil save-command entity-id])
         :on-cancel    #(>evt [::ee/cancel-changes entity-id])
         :confirm-opts {:class-name "border-0"
                        :text       (tr [:save])}
         :cancel-opts  {:class-name "border-0"
                        :text       (tr [:cancel])}}]]
      (cc/button {:kind     :ghost
                  :size     :small
                  :disabled editing-disabled?
                  :on-click #(>evt [::ee/set-values entity-id {attr entity-attr}])
                  :icon     i/pen}
                 (tr [rename-kw])))))

(defn item-group-editor-header
  "Editable item group name and editing/reordering buttons"
  [{:keys [tr language]} item-group]
  (let [item-group-id      (:db/id item-group)
        unsaved-item-group (<sub [::ee/unsaved-for item-group-id])
        prev-in-order      (<sub [::prev-item-group-template (:ordered/order item-group)])
        next-in-order      (<sub [::next-item-group-template (:ordered/order item-group)])
        editing-disabled?  (<sub [::checklist-subs/reordering?])]
    [:div.p-2.flex.flex-row.items-baseline.bg-white.rounded-sm.justify-between
     [:div.flex.items-baseline.flex-row.gap-4
      [:div.bold.uppercase
       [:div.py-1.w-12.text-center.align-center.mr-4.inline-block.bg-black.text-white.rounded-sm
        (<sub [::group-template-item-count item-group-id])]
       (localized-attr item-group :inspection.checklist-item.group/names language)]
      [i18n-popup-text-editor
       (util/mmap {:title-kw     :inspection.checklist-item.group/names
                   :rename-kw    :inspection.checklist-item.group/edit
                   :entity       item-group
                   :attr         :inspection.checklist-item.group/names
                   :save-command :inspection/edit-checklist-item-group-template}
                  tr editing-disabled?)]]
     [:div.flex.flex-row.justify-between.items-end
      [ui/modal-confirmation-button
       (merge {:tr           tr
               :id           (str "delete-checklist-template-child-" item-group-id)
               :disabled?    (or editing-disabled? (some? unsaved-item-group))
               :on-yes-click #(>evt [::delete-item-group-template item-group-id])}
              {:trigger-button-text (tr [:inspection.checklist-item.group/delete])
               :trigger-button-opts {:kind :ghost
                                     :size :small
                                     :icon i/trash}})]
      (when (<sub [::checklist-subs/reordering?])
        [:<>
         [ui-ib/icon-button
          {:icon        i/arrow-up
           :disabled?   (nil? prev-in-order)
           :on-click    #(>evt [::reorder-item-group-templates item-group prev-in-order])
           :description (tr [:move-up])}]
         [ui-ib/icon-button
          {:icon        i/arrow-down
           :disabled?   (nil? next-in-order)
           :on-click    #(>evt [::reorder-item-group-templates item-group next-in-order])
           :description (tr [:move-down])}]])]]))

(defn group-items-editor
  "List of inspection item template editors for the given item group"
  [{:keys [tr] :as ctx} {item-group-id :db/id}]
  (let [group-items (<sub [::group-template-items item-group-id])]
    [:<>
     [item-column-headers ctx]
     [dnd/wrap-dnd
      {:id               (str "checklist-item-templates-dnd-" item-group-id)
       :dnd-group        :checklist-item-templates
       :items            group-items
       :props-for-source {:class "translate-x-2"}
       :on-reorder       #(>evt [::reorder-item-templates %1 %2 %3])
       :on-insert        #(>evt [::move-item-template item-group-id (:db/id %)])}
      (fn [{:keys [augment-draggable-props augment-target-props sorted]}]
        (if (seq sorted)
          (futil/for-frag [item sorted]
            [item-editor
             (merge ctx {:container-opts (augment-target-props {} item)
                         :draggable-opts (augment-draggable-props {} item)})
             item])
          ;; Empty dropzone for item templates from other item-group-templates
          [:div.my-6.text-gray-600
           (augment-target-props {} nil)
           (tr [:inspection.checklist-item/empty])]))]]))

(defn item-group-editor
  "Editor for the item group template and for reordering or adding new checklist item templates under it"
  [{:keys [tr language] :as ctx} item-group]
  [:<>
   [item-group-editor-header ctx item-group]
   [group-items-editor ctx item-group]
   (when-not (<sub [::checklist-subs/reordering?])
     (cc/button {:kind       :primary
                 :size       :small
                 :icon       i/plus
                 :class-name "mt-4"
                 :on-click   #(>evt [::create-item-template
                                     (-> item-group :entity/parent :db/id)
                                     (:db/id item-group)
                                     language])}
                (tr [:inspection.checklist-item/add-new])))
   [ui-sep/horizontal-line]])

(defn editor
  "List of inspection checklist item template editors, grouped under their respective item groups"
  [{:keys [tr language]}]
  (let [item-groups (<sub [::item-group-templates])
        checklist   (<sub [::checklist-subs/selected-checklist-template])
        ctx         (util/mmap tr language)]
    [:div.mt-12
     (if (seq item-groups)
       (futil/for-frag [item-group (<sub [::item-group-templates])]
         [item-group-editor ctx item-group])
       [:div.my-6.text-gray-600 (tr [:inspection.checklist-item.group/empty])])
     (when-not (<sub [::checklist-subs/reordering?])
       (cc/button {:kind     :tertiary
                   :size     :small
                   :icon     i/plus
                   :on-click #(>evt [::create-item-group-template (:db/id checklist) language])}
                  (tr [:inspection.checklist-item.group/add-new])))]))
